import _ from 'lodash';
import { Link } from 'react-router-dom';
import { useMemo, useCallback } from 'react';

import { Box, Stack, useMediaQuery } from '@mui/system';
import {
  Card,
  TableRow,
  useTheme,
  TableCell,
  CardHeader,
  Typography,
  CardContent,
  CardActionArea
} from '@mui/material';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { useTranslate } from 'src/locales';
import { ApplicationStage } from 'src/services/applications/applications.types';
import { ICandidateApplication } from 'src/services/candidates/candidates.types';
import { RoleStatus, JobVerificationStatus } from 'src/services/jobs/jobs.types';

import Iconify from 'src/components/iconify';
import DateDisplay from 'src/components/date/date-display';
import NestedMenuWithTrigger from 'src/components/menu/nested-menu';
import RoleStatusLabel from 'src/components/role-status-label/role-status-label';
import TruncatedTextField from 'src/components/truncate-text/truncated-text-field';

import { ApplicationStageLabel } from 'src/sections/jobs/applications/applications-stage-label';
import { JobVerificationStatusIcon } from 'src/sections/jobs/applications/jobs-verification-status-icon';

import { TenantType } from 'src/types/enums';

type JobTableRowProps = {
  row: ICandidateApplication;
  disableControls?: boolean;
};

export default function JobTableRow({ row, disableControls = false }: JobTableRowProps) {
  const { t } = useTranslate();

  const tenant = useOrgTenant();

  const router = useRouter();

  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Show the real stage. Only a rejection shows how far the candidate got before it (JT1-1689).
  const displayStage = (
    row.stage === ApplicationStage.REJECTED ? (row.pre_finalization_stage ?? row.stage) : row.stage
  ) as ApplicationStage;

  const viewDetail = useCallback(() => {
    router.push(`${paths.dashboard.jobs.root}/${row.job.id}`);
  }, [router, row.job.id]);

  const menuItems = useMemo(() => {
    const items = [];

    items.push({
      label: t('common.view'),
      onClick: viewDetail,
      icon: <Iconify icon="solar:eye-bold" />,
    });

    return items;
  }, [t, viewDetail]);

  if (isMobile) {
    return (
      <Card sx={{ mx: 2, mb: 1 }}>
        <CardActionArea>
          <CardHeader
            title={
              <Link
                to={`${paths.dashboard.jobs.root}/${row.job.id}`}
                style={{
                  color: theme.palette.text.primary,
                  textDecoration: 'none',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Link
                    to={`${paths.dashboard.jobs.root}/${row.job.id}`}
                    style={{
                      color: theme.palette.text.primary,
                      textDecoration: 'none',
                      textWrap: 'nowrap',
                      textTransform: 'capitalize',
                    }}
                  >
                    <TruncatedTextField
                      text={_.capitalize(row.job.title)}
                      mode="characters"
                      limit={25}
                      showMoreEnabled={false}
                      variant="caption"
                      fontWeight="bold"
                      gutterBottom
                      style={{ textTransform: 'capitalize' }}
                    />
                  </Link>
                </Stack>
              </Link>
            }
            subheader={
              [TenantType.Recruiter].includes(tenant)
                ? row.job.client_company_name || row.job.client?.company_name
                : ''
            }
          />
          <CardContent sx={{ position: 'relative', pr: 6, cursor: 'pointer' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <ApplicationStageLabel status={displayStage} />
            </Stack>

            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 50 }}>
              <NestedMenuWithTrigger
                menuItems={menuItems}
                trigger={{ type: 'icon', icon: <Iconify icon="eva:more-vertical-fill" /> }}
              />
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    );
  }

  return (
    <TableRow>
      <TableCell>
        <Stack direction="row" spacing={1} justifyContent="start" alignItems="center">
          <JobVerificationStatusIcon status={row.job.verification_status as JobVerificationStatus} />
          <Link
            to={`${paths.dashboard.jobs.root}/${row.job.id}`}
            style={{
              color: theme.palette.text.primary,
              fontWeight: 'bold',
              textWrap: 'nowrap',
              textTransform: 'capitalize',
            }}
          >
            <TruncatedTextField
              text={row.job.title}
              mode="characters"
              limit={30}
              showMoreEnabled={false}
              variant="subtitle2"
              fontWeight="bold"
              style={{ textTransform: 'capitalize' }}
            />
          </Link>
        </Stack>
      </TableCell>

      {[TenantType.Recruiter].includes(tenant) && (
        <TableCell>
          {row.job.client_profile?.id ? (
            <Stack direction="row" spacing={1}>
              <Link
                to={paths.dashboard.companies.detail(row.job.client_profile.id, tenant, TenantType.Client)}
                className="text-gray-800"
              >
                {row.job.client?.company_name || row.job.client_company_name}
              </Link>
            </Stack>
          ) : (
            <Typography noWrap variant="subtitle2">
              {_.isEmpty(row.job.client_company_name) ? '-' : row.job.client_company_name}
            </Typography>
          )}
        </TableCell>
      )}

      <TableCell>
        <RoleStatusLabel status={row.job.role_status as RoleStatus} />
      </TableCell>

      <TableCell>
        <ApplicationStageLabel status={displayStage} />
      </TableCell>

      <TableCell>
        <DateDisplay date={row.updated_at} variant='body2' format='DD/MM/YYYY' />
      </TableCell>

      <TableCell align="right" sx={{ pr: 1 }}>
        <NestedMenuWithTrigger
          menuItems={menuItems}
          trigger={{ type: 'icon', icon: <Iconify icon="eva:more-horizontal-fill" /> }}
        />
      </TableCell>
    </TableRow>
  );
}
